import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Box, FormHelperText, FormLabel } from '@mui/material'
import Toolbar from './Toolbar'
import HtmlDialog from './HtmlDialog'
import { isBlank, sanitizeHtml } from './emailHtml'

const COMMANDS = {
  bold: 'bold',
  italic: 'italic',
  underline: 'underline',
  bulletList: 'insertUnorderedList',
  orderedList: 'insertOrderedList',
  clear: 'removeFormat',
  undo: 'undo',
  redo: 'redo',
}

const queryState = command => {
  try {
    return document.queryCommandState(command)
  } catch {
    return false
  }
}

const moveCaretToEnd = host => {
  const selection = document.getSelection()

  if (!selection) return

  const range = document.createRange()

  range.selectNodeContents(host)
  range.collapse(false)
  selection.removeAllRanges()
  selection.addRange(range)
}

const escapeText = text =>
  String(text ?? '')
    .replace(/[&<>]/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[character])
    .replace(/\n/g, '<br />')

const RichTextEditor = forwardRef(function RichTextEditor(
  { value, onChange, label, helperText, error, minHeight = 260, placeholder, disabled },
  ref,
) {
  const hostRef = useRef(null)
  // what the DOM is known to hold. emit() compares against it so it can never hand back content the
  // parent has already replaced — closing a popover over the editor blurs it mid-commit, before the
  // effect below has written the new value, and an unguarded blur reports the stale html as an edit
  const lastHtmlRef = useRef(value || '')
  const [htmlOpen, setHtmlOpen] = useState(false)
  const [active, setActive] = useState({})

  useEffect(() => {
    try {
      document.execCommand('defaultParagraphSeparator', false, 'p')
    } catch {
      /* unsupported — serialisation handles the difference */
    }
  }, [])

  useEffect(() => {
    const host = hostRef.current

    if (!host) return

    const incoming = value || ''

    if (incoming === host.innerHTML) {
      lastHtmlRef.current = host.innerHTML

      return
    }

    const wasFocused = host === document.activeElement || host.contains(document.activeElement)

    host.innerHTML = incoming
    // read it back rather than storing `incoming`: the browser normalises what it was given, and a
    // comparison against the raw string would read as an edit on the next blur
    lastHtmlRef.current = host.innerHTML

    // assigning innerHTML drops the selection, so a replacement made while the editor has focus
    // would leave the caret at the top of the message
    if (wasFocused) {
      host.focus()
      moveCaretToEnd(host)
    }
  }, [value])

  const emit = useCallback(() => {
    const html = hostRef.current?.innerHTML ?? ''

    if (html === lastHtmlRef.current) return

    lastHtmlRef.current = html
    onChange?.(html)
  }, [onChange])

  const refreshActive = useCallback(() => {
    const selection = document.getSelection()
    const anchor = selection?.anchorNode ?? null
    const host = hostRef.current

    let linkHref = null

    for (let node = anchor; node && host?.contains(node); node = node.parentNode) {
      if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'A') {
        linkHref = node.getAttribute('href')
        break
      }
    }

    let block = ''

    try {
      block = String(document.queryCommandValue('formatBlock') ?? '')
    } catch {
      block = ''
    }

    setActive({
      bold: queryState('bold'),
      italic: queryState('italic'),
      underline: queryState('underline'),
      bulletList: queryState('insertUnorderedList'),
      orderedList: queryState('insertOrderedList'),
      heading: /^h3$/i.test(block),
      link: Boolean(linkHref),
      linkHref,
    })
  }, [])

  useEffect(() => {
    const onSelectionChange = () => {
      const host = hostRef.current
      const anchor = document.getSelection()?.anchorNode ?? null

      if (host && anchor && host.contains(anchor)) refreshActive()
    }

    document.addEventListener('selectionchange', onSelectionChange)

    return () => document.removeEventListener('selectionchange', onSelectionChange)
  }, [refreshActive])

  const run = useCallback(
    (command, argument) => {
      const host = hostRef.current

      if (!host || disabled) return

      host.focus()

      try {
        document.execCommand(COMMANDS[command] ?? command, false, argument)
      } catch {
        return
      }

      emit()
      refreshActive()
    },
    [disabled, emit, refreshActive],
  )

  const toggleHeading = useCallback(() => run('formatBlock', active.heading ? 'p' : 'h3'), [run, active.heading])

  const setLink = useCallback(
    href => (href ? run('createLink', href) : run('unlink')),
    [run],
  )

  const onPaste = useCallback(
    event => {
      event.preventDefault()

      const clipboard = event.clipboardData
      const html = clipboard?.getData('text/html')
      const content = html ? sanitizeHtml(html) : escapeText(clipboard?.getData('text/plain') ?? '')

      try {
        document.execCommand('insertHTML', false, content)
      } catch {
        return
      }

      emit()
    },
    [emit],
  )

  useImperativeHandle(
    ref,
    () => ({
      insertText: text => {
        hostRef.current?.focus()
        document.execCommand('insertHTML', false, escapeText(text))
        emit()
      },
      insertHtml: html => {
        hostRef.current?.focus()
        document.execCommand('insertHTML', false, sanitizeHtml(html))
        emit()
      },
      focus: () => hostRef.current?.focus(),
    }),
    [emit],
  )

  return (
    <Box>
      {label && (
        <FormLabel error={error} sx={{ display: 'block', mb: 0.5, fontSize: 13 }}>
          {label}
        </FormLabel>
      )}

      <Box
        sx={{
          border: '1px solid',
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          '&:focus-within': { borderColor: error ? 'error.main' : 'primary.main' },
        }}
      >
        <Toolbar
          active={active}
          disabled={disabled}
          onCommand={run}
          onToggleHeading={toggleHeading}
          onLink={setLink}
          onInsertHtml={() => setHtmlOpen(true)}
        />

        <Box
          ref={hostRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label={label}
          onInput={emit}
          onBlur={emit}
          onPaste={onPaste}
          onKeyUp={refreshActive}
          onMouseUp={refreshActive}
          data-placeholder={placeholder ?? ''}
          data-empty={isBlank(value) ? 'true' : 'false'}
          sx={{
            p: 1.5,
            minHeight,
            maxHeight: 520,
            overflow: 'auto',
            outline: 'none',
            cursor: disabled ? 'default' : 'text',
            '& > :first-of-type': { mt: 0 },
            '& > :last-child': { mb: 0 },
            '& p': { my: 1 },
            '& h3': { my: 1.5, fontSize: 17 },
            '& ul, & ol': { my: 1, pl: 3 },
            '& blockquote': { my: 1, pl: 1.5, borderLeft: '3px solid', borderColor: 'divider' },
            '& a': { color: 'primary.main' },
            '&[data-empty="true"]::before': {
              content: 'attr(data-placeholder)',
              color: 'text.disabled',
              pointerEvents: 'none',
              display: 'block',
            },
          }}
        />
      </Box>

      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}

      <HtmlDialog
        open={htmlOpen}
        onClose={() => setHtmlOpen(false)}
        onInsert={html => {
          hostRef.current?.focus()
          document.execCommand('insertHTML', false, html)
          emit()
        }}
      />
    </Box>
  )
})

export default RichTextEditor
